import React, {
  createContext,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { datadogLogs } from '@datadog/browser-logs';
import { nonNull } from '@helpers/non-null';
import {
  signInAsExternalAwReviewer,
  signInExternalReviewer,
  signUpAsExternalAwReviewer,
  signUpExternalReviewer
} from '@api/ExternalReviewer';
import { CompleteOnboardingStepDto, ExternalReviewerDto } from '@api/Api';
import { MixpanelService } from '@services/mixpanelService';
import { IAuthContext, User, AuthError } from '@context/AuthContext/types';
import { combineUserName } from '@helpers/combineUserName';
import { IGNORE_NON_LTI_LOGIN_ERROR_KEY } from '@config/constants';

export const AuthContext = createContext<
  IAuthContext<boolean, 'external-reviewer' | 'internal'>
>(undefined as any);

interface AuthProviderProps {
  readonly children: ReactNode;
}

const EXTERNAL_REVIEWER_KEY = 'external-reviewer';

function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1];
  const padded =
    base64Url.replace(/-/g, '+').replace(/_/g, '/') +
    '='.repeat((4 - (base64Url.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return JSON.parse(new TextDecoder().decode(bytes));
}

function mapExternalUser(
  reviewer: ExternalReviewerDto
): User<'external-reviewer'> {
  const completedOnboardingStepsKey = `streamwork://${reviewer.email}/completed-onboarding-steps`;
  const completedOnboardingSteps = (localStorage
    .getItem(completedOnboardingStepsKey)
    ?.split(',') ?? []) as unknown as CompleteOnboardingStepDto['type'][];
  return {
    type: 'external-reviewer',
    id: reviewer.id,
    createdAt: null,
    canChangePassword: null,
    pictureUrl: reviewer.picture.url ?? null,
    email: reviewer.email,
    emailVerified: false,
    firstName: reviewer.firstName,
    lastName: reviewer.lastName ?? null,
    name: combineUserName(reviewer),
    companyName: null,
    phone: null,
    timeZone: null,
    notificationFrequency: null,
    twoStepVerificationEnabled: false,
    rememberMe: true,
    isNewUser: null,
    primaryProviderId: 'external',
    providerId: 'external',
    completedOnboardingSteps,
    slackConnected: false,
    metadata: {},
    geoip: null
  };
}

function mapAuth0User(auth0User: any): User<'internal'> {
  return {
    type: 'internal',
    id: nonNull(auth0User.app_user_id),
    createdAt: nonNull(auth0User.created_at) ?? null,
    canChangePassword: nonNull(auth0User.can_change_password),
    pictureUrl: auth0User.picture ?? null,
    email: nonNull(auth0User.email),
    emailVerified: nonNull(auth0User.email_verified),
    firstName: nonNull(auth0User.given_name),
    lastName: auth0User.family_name ?? null,
    name: combineUserName({
      firstName: nonNull(auth0User.given_name),
      lastName: auth0User.family_name ?? null
    }),
    companyName: auth0User.company_name ?? null,
    phone: auth0User.phone ?? null,
    timeZone: auth0User.time_zone ?? null,
    notificationFrequency: auth0User.notification_frequency ?? null,
    twoStepVerificationEnabled: nonNull(
      auth0User.two_step_verification_enabled
    ),
    rememberMe: auth0User.remember_me ?? null,
    isNewUser: auth0User.is_new_user ?? null,
    primaryProviderId: nonNull(auth0User.primary_provider_id),
    providerId: nonNull(auth0User.current_provider_id),
    completedOnboardingSteps: auth0User.completed_onboarding_steps,
    slackConnected: auth0User.slack_connected ?? null,
    metadata: auth0User.metadata ?? {},
    geoip: auth0User.geoip ?? null
  };
}

function updateProfile<T extends 'external-reviewer' | 'internal'>(
  profile: Partial<Omit<User<T>, 'type' | 'email' | 'rememberMe'>>,
  user: User<T> | null
): User<T> | null {
  if (!user) return null;
  const updated = { ...user };
  if (profile.firstName !== undefined) {
    updated.firstName = profile.firstName;
  }
  if (profile.lastName !== undefined) {
    updated.lastName = profile.lastName;
  }
  if (profile.emailVerified !== undefined) {
    updated.emailVerified = profile.emailVerified;
  }
  if (profile.companyName !== undefined) {
    updated.companyName = profile.companyName;
  }
  if (profile.phone !== undefined) {
    updated.phone = profile.phone;
  }
  if (profile.timeZone !== undefined) {
    updated.timeZone = profile.timeZone;
  }
  if (profile.notificationFrequency !== undefined) {
    updated.notificationFrequency = profile.notificationFrequency;
  }
  if (profile.pictureUrl !== undefined) {
    updated.pictureUrl = profile.pictureUrl;
  }
  if (profile.twoStepVerificationEnabled !== undefined) {
    updated.twoStepVerificationEnabled = profile.twoStepVerificationEnabled;
  }
  if (profile.slackConnected !== undefined) {
    updated.slackConnected = profile.slackConnected;
  }
  updated.name = combineUserName(updated);
  if (profile.completedOnboardingSteps !== undefined) {
    updated.completedOnboardingSteps = profile.completedOnboardingSteps;
    const completedOnboardingStepsKey = `streamwork://${updated.email}/completed-onboarding-steps`;
    localStorage.setItem(
      completedOnboardingStepsKey,
      updated.completedOnboardingSteps.join(',')
    );
  }
  if (profile.metadata !== undefined) {
    updated.metadata = profile.metadata;
  }
  return updated;
}

function uppyCompanionLogout() {
  for (let i = 0; i < localStorage.length; ++i) {
    const key = localStorage.key(i);
    if (key?.startsWith('companion-') && key?.endsWith('-auth-token')) {
      localStorage.removeItem(key);
    }
  }
}

function UxpAuthProvider({
  uxpHost,
  children
}: AuthProviderProps & { uxpHost: any }) {
  const [auth, setAuth] = useState<IAuthContext<boolean, 'internal'>>(() => {
    return {
      isLoading: true,
      isAuthenticated: false,
      user: null,
      error: null,
      getAccessToken: async (ignoreCache) => {
        const promise = new Promise<string>((resolve, reject) => {
          function onMessage(e: MessageEvent) {
            if (e.data?.type === 'tokens') {
              try {
                const { accessToken } = e.data;
                resolve(accessToken);
              } catch (err) {
                reject(err);
              } finally {
                window.removeEventListener('message', onMessage);
              }
            }
          }
          window.addEventListener('message', onMessage);
        });
        uxpHost.postMessage({
          type: 'get-tokens',
          ignoreCache
        });
        return promise;
      },
      login: () => {
        throw new Error('Not implemented');
      },
      logout: () => {
        uxpHost.postMessage({ type: 'logout' });
      },
      loginForAW: () => {
        throw new Error('Not implemented');
      },
      signUpForAW: () => {
        throw new Error('Not implemented');
      },
      loginForReview: () => {
        throw new Error('Not implemented');
      },
      signUpForReview: () => {
        throw new Error('Not implemented');
      },
      updateUserProfile: (profile) => {
        setAuth((prev) => ({
          ...prev,
          user: updateProfile(profile, prev.user)
        }));
        uxpHost.postMessage({ type: 'expire-tokens' });
      }
    };
  });
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.data?.type === 'tokens') {
        const { idToken } = e.data;
        const user = mapAuth0User(decodeJwtPayload(idToken));
        setAuth((prev) => ({
          ...prev,
          isLoading: false,
          isAuthenticated: true,
          user
        }));
      }
    }
    window.addEventListener('message', onMessage);
    uxpHost.postMessage({ type: 'get-tokens' });
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [uxpHost]);
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

function DefaultAuthProvider({ children }: AuthProviderProps) {
  const auth0 = useAuth0();
  const auth0LoginRef = useRef(auth0.loginWithRedirect);
  const auth0LogoutRef = useRef(auth0.logout);
  auth0LoginRef.current = auth0.loginWithRedirect;
  auth0LogoutRef.current = auth0.logout;

  const [externalUser, setExternalUser] = useState<
    | { error: null; isLoading: true }
    | {
        error: null;
        isLoading: boolean;
        user: User<'external-reviewer'>;
        assetReviewId: string | null;
        awId: string | null;
        accessToken: string;
        expiresAt: Date;
      }
    | { error: AuthError; isLoading: boolean }
  >();

  const [auth, setAuth] = useState<
    IAuthContext<boolean, 'external-reviewer' | 'internal'>
  >({
    isLoading: true,
    isAuthenticated: false,
    user: null,
    error: null,
    getAccessToken: null,
    logout: (options) => {
      localStorage.removeItem(EXTERNAL_REVIEWER_KEY);
      localStorage.removeItem(IGNORE_NON_LTI_LOGIN_ERROR_KEY);
      for (let i = 0; i < localStorage.length; ++i) {
        const key = localStorage.key(i);
        if (key?.startsWith('asset-review-password-')) {
          localStorage.removeItem(key);
        }
      }
      uppyCompanionLogout();
      setExternalUser(undefined);
      auth0LogoutRef.current(options);
    },
    login: async (options) => {
      localStorage.removeItem(EXTERNAL_REVIEWER_KEY);
      localStorage.removeItem(IGNORE_NON_LTI_LOGIN_ERROR_KEY);
      uppyCompanionLogout();
      return await auth0LoginRef.current(options);
    },
    loginForReview: async (payload) => {
      auth0LogoutRef.current({ openUrl: false });
      uppyCompanionLogout();
      setExternalUser({ isLoading: true, error: null });
      try {
        const { data } = await signInExternalReviewer(payload);
        setExternalUser({
          error: null,
          isLoading: false,
          assetReviewId: payload.assetReviewId,
          awId: null,
          accessToken: data.accessToken,
          expiresAt: moment(data.expiresAt).toDate(),
          user: mapExternalUser(data.reviewer)
        });
      } catch (err) {
        const errorCode = (err as any).response?.data?.errorCode;
        if (errorCode === 'internal_user_exists') {
          await auth0LoginRef.current({
            authorizationParams: {
              prompt: 'login',
              login_hint: payload.email
            },
            appState: {
              returnTo: window.location.pathname + window.location.search
            }
          });
        } else {
          (err as any).error = errorCode || 'unknown';
          (err as any).status = (err as any).response?.status;
          setExternalUser({ error: err as AuthError, isLoading: false });
        }
      }
    },
    signUpForReview: async (payload) => {
      auth0LogoutRef.current({ openUrl: false });
      uppyCompanionLogout();
      setExternalUser({ isLoading: true, error: null });
      try {
        const { data } = await signUpExternalReviewer(payload);
        setExternalUser({
          error: null,
          isLoading: false,
          assetReviewId: payload.assetReviewId,
          awId: null,
          accessToken: data.accessToken,
          expiresAt: moment(data.expiresAt).toDate(),
          user: mapExternalUser(data.reviewer)
        });
      } catch (err) {
        const errorCode = (err as any).response?.data?.errorCode;
        (err as any).error = errorCode || 'unknown';
        (err as any).status = (err as any).response?.status;
        setExternalUser({ error: err as AuthError, isLoading: false });
      }
    },
    loginForAW: async (payload) => {
      auth0LogoutRef.current({ openUrl: false });
      uppyCompanionLogout();
      setExternalUser({ isLoading: true, error: null });
      try {
        const { data } = await signInAsExternalAwReviewer(payload);
        setExternalUser({
          error: null,
          isLoading: false,
          assetReviewId: null,
          awId: payload.awId,
          accessToken: data.accessToken,
          expiresAt: moment(data.expiresAt).toDate(),
          user: mapExternalUser(data.reviewer)
        });
      } catch (err) {
        const errorCode = (err as any).response?.data?.errorCode;
        if (errorCode === 'internal_user_exists') {
          await auth0LoginRef.current({
            authorizationParams: {
              prompt: 'login',
              login_hint: payload.email
            },
            appState: {
              returnTo: window.location.pathname + window.location.search
            }
          });
        } else {
          (err as any).error = errorCode || 'unknown';
          (err as any).status = (err as any).response?.status;
          setExternalUser({ error: err as AuthError, isLoading: false });
        }
      }
    },
    signUpForAW: async (payload) => {
      auth0LogoutRef.current({ openUrl: false });
      uppyCompanionLogout();
      setExternalUser({ isLoading: true, error: null });
      try {
        const { data } = await signUpAsExternalAwReviewer(payload);
        setExternalUser({
          error: null,
          isLoading: false,
          assetReviewId: null,
          awId: payload.awId,
          accessToken: data.accessToken,
          expiresAt: moment(data.expiresAt).toDate(),
          user: mapExternalUser(data.reviewer)
        });
      } catch (err) {
        const errorCode = (err as any).response?.data?.errorCode;
        (err as any).error = errorCode || 'unknown';
        (err as any).status = (err as any).response?.status;
        setExternalUser({ error: err as AuthError, isLoading: false });
      }
    },
    updateUserProfile: (profile) => {
      setAuth((prev) => ({ ...prev, user: updateProfile(profile, prev.user) }));
    }
  });

  useEffect(() => {
    if (auth0.isLoading || auth0.isAuthenticated || auth0.error) {
      setAuth((prev) => ({
        ...prev,
        isLoading: auth0.isLoading,
        isAuthenticated: auth0.isAuthenticated,
        user: auth0.user ? mapAuth0User(auth0.user) : null,
        error: (auth0.error as any) ?? null,
        getAccessToken: auth0.isAuthenticated
          ? (ignoreCache) =>
              auth0.getAccessTokenSilently(
                ignoreCache ? { cacheMode: 'off' } : {}
              )
          : null
      }));
      if (!auth0.isLoading && !auth0.isAuthenticated) {
        uppyCompanionLogout();
      }
    } else if (externalUser?.isLoading) {
      setAuth((prev) => ({
        ...prev,
        isLoading: true,
        isAuthenticated: false,
        user: null,
        error: externalUser.error,
        getAccessToken: null
      }));
    } else if (externalUser?.error) {
      setAuth((prev) => ({
        ...prev,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: externalUser.error,
        getAccessToken: null
      }));
    } else if (externalUser) {
      setAuth((prev) => ({
        ...prev,
        isLoading: false,
        isAuthenticated: true,
        user: externalUser.user,
        error: null,
        getAccessToken: async () => {
          const now = new Date();
          const minutesLeft =
            (externalUser.expiresAt.valueOf() - now.valueOf()) / 60000;
          if (minutesLeft > 5) return externalUser.accessToken;
          if (externalUser.assetReviewId) {
            const { data } = await signInExternalReviewer({
              email: externalUser.user.email,
              assetReviewId: externalUser.assetReviewId
            });
            setExternalUser((prev) => {
              if (!prev || prev.isLoading || prev.error) return prev;
              return {
                ...prev,
                accessToken: data.accessToken,
                expiresAt: moment(data.expiresAt).toDate()
              };
            });
            return data.accessToken;
          }
          if (externalUser.awId) {
            const { data } = await signInAsExternalAwReviewer({
              email: externalUser.user.email,
              awId: externalUser.awId
            });
            setExternalUser((prev) => {
              if (!prev || prev.isLoading || prev.error) return prev;
              return {
                ...prev,
                accessToken: data.accessToken,
                expiresAt: moment(data.expiresAt).toDate()
              };
            });
            return data.accessToken;
          }
          return externalUser.accessToken;
        }
      }));
    } else {
      setAuth((prev) => ({
        ...prev,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: null,
        getAccessToken: null
      }));
    }
  }, [
    auth0.isLoading,
    auth0.isAuthenticated,
    auth0.user,
    auth0.error,
    auth0.getAccessTokenSilently,
    auth0.logout,
    externalUser
  ]);

  const { loginForReview, loginForAW } = auth;
  useEffect(() => {
    const json = localStorage.getItem(EXTERNAL_REVIEWER_KEY);
    if (!json) return;
    try {
      const { email, assetReviewId, awId } = JSON.parse(json);
      if (email && assetReviewId) {
        loginForReview({ email, assetReviewId });
        return;
      }
      if (email && awId) {
        loginForAW({ email, awId });
      }
    } catch {
      localStorage.removeItem(EXTERNAL_REVIEWER_KEY);
    }
  }, [loginForReview, loginForAW]);

  useEffect(() => {
    if (!externalUser || externalUser.isLoading || externalUser.error) return;
    localStorage.setItem(
      EXTERNAL_REVIEWER_KEY,
      JSON.stringify({
        email: externalUser.user.email,
        assetReviewId: externalUser.assetReviewId,
        awId: externalUser.awId
      })
    );
  }, [externalUser]);

  const prevAuth = useRef<typeof auth | undefined>(undefined);
  if (auth !== prevAuth.current) {
    prevAuth.current = auth;
    if (!auth.isAuthenticated) {
      datadogLogs.removeGlobalContextProperty('usr.id');
      datadogLogs.removeGlobalContextProperty('usr.email');
      datadogLogs.removeGlobalContextProperty('usr.name');
      MixpanelService.reset();
    } else {
      const user = nonNull(auth.user);
      datadogLogs.setGlobalContextProperty('usr.id', user.id);
      datadogLogs.setGlobalContextProperty('usr.email', user.email);
      datadogLogs.setGlobalContextProperty('usr.name', combineUserName(user));
      datadogLogs.setGlobalContextProperty('usr.type', user.type);
      MixpanelService.identify(user);
      // eslint-disable-next-line no-multi-assign
      const hubspot = ((window as any)._hsq = (window as any)._hsq || []);
      hubspot.push(['identify', { email: user.email }]);
      hubspot.push(['trackPageView']);
    }
  }
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: AuthProviderProps) {
  if ((window as any).uxpHost) {
    return (
      <UxpAuthProvider uxpHost={(window as any).uxpHost}>
        {children}
      </UxpAuthProvider>
    );
  }
  return <DefaultAuthProvider>{children}</DefaultAuthProvider>;
}
